import { Chart } from "chart.js";
import React, { useEffect, useMemo, useRef } from "react";

import { Spinner } from "@performyard/py-components";

import EmptyChart from "../../../../../../../../components/EmptyChart";
// utils
import Config from "./Config";

import { ChartOptions } from "../../../../../../schemas/report";

import useStore from "../../../../store";
import { ChartType, nineBoxRequiredFields } from "../../../../store/enums";

import { nineBoxBackground, scaleArrowsAxes } from "./utils";

// components
import ScatterChart from "../ChartHooks/ScatterChart/index";
// hooks
import useNineBoxChartData from "../ChartHooks/ScatterChart/useScatterData";
import useChartOptions from "../ChartHooks/UseChartOptions/useChartOptions";
import { useDownloadImage } from "../ChartHooks/UseDownloadImage/useDownloadImage";
import { ChartContainer, VisualizationHeader } from "../Layout";
// styles
import css from "../Layout/Layout.module.scss";
import OptionSettings from "../shared/OptionSettings";

const { dataSeriesRangeOptions } = OptionSettings[ChartType.ScatterChart];

const NineBoxChart = () => {
	const [enabledSummarizeFields] = useStore((state) => [state.enabledSummarizeFields]);
	const chartRef = useRef<Chart>(null);

	const {
		scatterChartData: nineBoxChartData,
		count,
		scaledOptions,
		isLoading,
	} = useNineBoxChartData({
		chartType: ChartType.NineBox,
		xAxisLabel: Config.X_AXIS_LABEL,
		yAxisLabel: Config.Y_AXIS_LABEL,
		scaleOptions: { x: { display: false }, y: { display: false } },
		padding: { top: 24, right: 16, bottom: 56, left: 64 },
	});

	const {
		dataSeriesOptions: _dataSeriesOptions,
		setVisualizeOptions,
		visualizeOption,
		previousVisualizeOption,
		previousChart,
		anyAxisOptions,
		selectDefaultXAxis,
		selectDefaultYAxis,
		fallbackToTableChart,
	} = useChartOptions(ChartType.NineBox);

	const formattedDescription = useMemo(() => {
		if (!nineBoxChartData || !count) {
			return "";
		}
		const numberOf = nineBoxChartData.datasets[0]?.data?.length;
		const dataSeriesOption = enabledSummarizeFields.find(
			(field) => field.value === visualizeOption?.dataSeries?.column.slice(8)
		)?.label;
		return `This chart is displaying ${numberOf} of ${count} ${
			dataSeriesOption ? `${dataSeriesOption} ` : ""
		} Items.`;
	}, [JSON.stringify(nineBoxChartData), count]);

	const { downloadImage } = useDownloadImage(chartRef, {
		chartType: ChartType.NineBox,
		description: formattedDescription,
		xAxisLabel: Config.X_AXIS_LABEL,
		yAxisLabel: Config.Y_AXIS_LABEL,
	});

	const dataSeriesOptions = _dataSeriesOptions.filter((option) =>
		nineBoxRequiredFields.has(option.value.toLowerCase())
	);

	const previousOptionInOptions = dataSeriesOptions.some(
		(option) => option.value === previousVisualizeOption?.dataSeries?.column
	);

	const carryOverDataSeriesOption = previousOptionInOptions ? previousVisualizeOption?.dataSeries?.column : null;

	useEffect(() => {
		setDefaultChartOptions();
	}, []);

	// this is an effect to listen to changes in the axis options
	// it finds the default value for the axis and sets it when the axis option is missing
	useEffect(() => {
		if (!anyAxisOptions?.some((option) => option.value === visualizeOption?.xAxis?.column)) {
			if (visualizeOption?.xAxis?.column) {
				setDefaultChartOptions(true, true);
			}
		}

		if (!anyAxisOptions?.some((option) => option.value === visualizeOption?.yAxis?.column)) {
			if (visualizeOption?.yAxis?.column) {
				setDefaultChartOptions(true, false);
			}
		}
	}, [anyAxisOptions?.length]);

	const setDefaultChartOptions = (forceUpdate = false, updateXAxis = false) => {
		const options: ChartOptions = {};
		if (dataSeriesOptions.length > 0) {
			const hasRangeOptionCarryover = dataSeriesRangeOptions.some(
				(option) => option.value === previousVisualizeOption?.dataSeries?.rangeOption
			);
			options.dataSeries = {
				column:
					carryOverDataSeriesOption ||
					visualizeOption?.dataSeries?.column ||
					dataSeriesOptions[dataSeriesOptions.length - 1].value,
				rangeOption: hasRangeOptionCarryover
					? previousVisualizeOption?.dataSeries?.rangeOption
					: visualizeOption?.dataSeries?.rangeOption || dataSeriesRangeOptions[2].value,
			};
		}
		if (anyAxisOptions.length > 1) {
			// handle initial value from carry over
			const carryOverFromX = previousVisualizeOption?.xAxis?.column;
			const carryOverFromY = previousVisualizeOption?.yAxis?.column;
			const carryOverMinFromX = previousVisualizeOption?.xAxis?.min;
			const carryOverMaxFromX = previousVisualizeOption?.xAxis?.max;
			const carryOverMinFromY = previousVisualizeOption?.yAxis?.min;
			const carryOverMaxFromY = previousVisualizeOption?.yAxis?.max;

			const currentXAxisOptions = [...anyAxisOptions];
			const currentYAxisOptions = [...anyAxisOptions];

			const defaultXAxisValue = { column: selectDefaultXAxis(ChartType.NineBox) };

			const defaultYAxisValue = { column: selectDefaultYAxis(ChartType.NineBox) };

			if (carryOverFromX) {
				const index = currentYAxisOptions.findIndex((option) => option.value === carryOverFromX);
				currentYAxisOptions.splice(index, 1);
			}

			if (carryOverFromY) {
				const index = currentXAxisOptions.findIndex((option) => option.value === carryOverFromY);
				currentXAxisOptions.splice(index, 1);
			}

			const columnX = carryOverFromX || defaultXAxisValue.column || currentXAxisOptions[0].value;

			const columnY = carryOverFromY || defaultYAxisValue.column || currentYAxisOptions[0].value;

			const minSuggestedX = anyAxisOptions.find((option) => option.value === columnX)?.metadata?.min;
			const maxSuggestedX = anyAxisOptions.find((option) => option.value === columnX)?.metadata?.max;
			const minSuggestedY = anyAxisOptions.find((option) => option.value === columnY)?.metadata?.min;
			const maxSuggestedY = anyAxisOptions.find((option) => option.value === columnY)?.metadata?.max;

			options.xAxis = {
				column: columnX,
				min: carryOverMinFromX || minSuggestedX || 0,
				max: carryOverMaxFromX || maxSuggestedX || 100,
				hasSuggestedMin: !!carryOverMinFromX || !!minSuggestedX,
				hasSuggestedMax: !!carryOverMaxFromY || !!maxSuggestedX,
			};

			options.yAxis = {
				column: columnY,
				min: carryOverMinFromY || minSuggestedY || 0,
				max: carryOverMaxFromY || maxSuggestedY || 100,
				hasSuggestedMin: !!carryOverMinFromY || !!minSuggestedY,
				hasSuggestedMax: !!carryOverMaxFromY || !!maxSuggestedY,
			};

			if (forceUpdate && updateXAxis) {
				options.xAxis.column = defaultXAxisValue.column || currentXAxisOptions[0].value;
			}

			if (forceUpdate && !updateXAxis) {
				options.yAxis.column = defaultYAxisValue.column || currentYAxisOptions[0].value;
			}
		}

		// current implementation: set default data and carry over previous chart options if they exist
		if (Object.keys(visualizeOption).length === 0 || forceUpdate) {
			const newVisualizeOptions = { [ChartType.NineBox]: { ...visualizeOption, ...options } };
			if (previousChart !== ChartType.NineBox) {
				newVisualizeOptions[previousChart] = null;
			}
			setVisualizeOptions(newVisualizeOptions);
		}

		// Current behavior: when there is visualization option on first render, check for missing data and fall back to table view
		if (Object.keys(visualizeOption).length > 0) {
			const hasDataSeriesColumn = dataSeriesOptions.some(
				(option) => option.value === visualizeOption.dataSeries?.column
			);

			// fallback to table view if there is not sufficient x and y axis options - Bugfix: PY-9653
			if (anyAxisOptions?.length < 2) {
				fallbackToTableChart();
				return;
			}

			const hasXAxisColumn = anyAxisOptions.some((option) => option.value === visualizeOption.xAxis?.column);
			const hasYAxisColumn = anyAxisOptions.some((option) => option.value === visualizeOption.yAxis?.column);

			if (!hasDataSeriesColumn) {
				options.dataSeries = {
					column: dataSeriesOptions[dataSeriesOptions.length - 1].value,
					rangeOption: dataSeriesRangeOptions[2].value,
				};
			}

			if (!hasXAxisColumn) {
				options.xAxis = {
					column: anyAxisOptions[0].value,
				};
			}

			if (!hasYAxisColumn) {
				options.yAxis = {
					column: anyAxisOptions[1].value,
				};
			}

			const newVisualizeOptions = { [ChartType.NineBox]: { ...visualizeOption, ...options } };
			setVisualizeOptions(newVisualizeOptions);
		}
	};

	return (
		<ChartContainer padding="16px 16px 16px 0px" height="calc(100% - 98px)" width="calc(100% - 4px)">
			{isLoading && !nineBoxChartData.datasets[0].data.length && (
				<div className={css.spinner}>
					<Spinner size={20} />
				</div>
			)}
			{!isLoading && nineBoxChartData.datasets[0].data.length > 0 && (
				<>
					<VisualizationHeader
						viewingCount={nineBoxChartData.datasets[0]?.data?.length || 0}
						totalCount={count}
						options={[
							{
								id: "download-image",
								label: "Download Image",
								action: downloadImage,
							},
						]}
					/>
					<ScatterChart
						ref={chartRef}
						data={nineBoxChartData}
						options={scaledOptions}
						plugins={[scaleArrowsAxes, nineBoxBackground]}
						dataTestId="nineBoxChart"
					/>
				</>
			)}
			{!isLoading && !nineBoxChartData.datasets[0].data.length && <EmptyChart type={ChartType.NineBox} />}
		</ChartContainer>
	);
};

export default NineBoxChart;
